import React, { Component } from 'react'
import styled from '@emotion/styled'
import {
  device,
  space,
  shadow,
  color,
  fontSize,
  fontWeight,
  transition,
  radius,
  easing,
} from '@ticketswap/solar'
import FeedbackDialog from '../FeedbackDialog'
import { Happy, Sad } from '@ticketswap/comets'
import { withTranslation } from '~/i18n'

const Container = styled.div`
  display: none;

  @media ${device.tablet} {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 50%;
    right: 0;
    z-index: 6;
    background-color: white;
    padding: ${space[4]};
    border-radius: ${space[8]} 0 0 ${space[8]};
    box-shadow: ${shadow.strong};
    transition-duration: 200ms;
    transition-timing-function: ${easing.easeOutCubic};
    transition-property: opacity, transform;
  }
`

const Button = styled.button`
  position: relative;
  border-radius: ${space[4]};
`

const IconContainer = styled.span`
  display: block;
  position: relative;
  color: ${color.spaceMediumAlpha};
  border-radius: ${space[4]};
  background-color: ${props =>
    props.variant === 'danger' ? color.mars : color.titan};
  transition: background-color ${transition};
  outline: 0;
  font-size: 0;
  line-height: 0;

  &:first-of-type {
    margin-bottom: ${space[4]};
  }
`

const Tooltip = styled.span`
  visibility: hidden;
  pointer-events: none;
  position: absolute;
  right: 100%;
  margin-right: ${space[16]};
  margin-top: ${space[8]};
  margin-bottom: ${space[8]};
  top: 0;
  bottom: 0;
  align-items: center;
  white-space: nowrap;
  font-size: ${fontSize[14]};
  font-weight: ${fontWeight.semiBold};
  opacity: 0;
  transform: translate3d(-0.5rem, 0, 0);
  transition: all ${transition};
  color: white;
  background-color: ${color.spaceLightAlpha};
  border-radius: ${radius.md};
  padding: ${space[8]} ${space[16]};

  span {
    display: inline-block;
    transform: translateY(1px);
  }

  ${Button}:hover &,
  ${Button}:focus & {
    visibility: visible;
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`

class FeedbackWidget extends Component {
  _isMounted = false
  observers = []
  state = {
    show: this.props.initialShow,
    observerEntries: {},
  }

  static defaultProps = {
    initialShow: false,
  }

  componentDidMount() {
    this._isMounted = true

    if (
      'IntersectionObserver' in window &&
      'IntersectionObserverEntry' in window
    ) {
      this.initObserver()
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.observerEntries !== this.state.observerEntries) {
      const { observerEntries } = this.state
      const values = []
      Object.keys(observerEntries).forEach(key =>
        values.push(observerEntries[key])
      )
      const show = values.filter(i => i === true).length === 0
      this.setState({ show })
    }
  }

  handleObserver = entries => {
    if (entries && entries.length) {
      const entry = entries[0]
      if (entry.isIntersecting) {
        this.setState(state => ({
          observerEntries: {
            ...state.observerEntries,
            [entry.target.classList[0]]: true,
          },
        }))
      } else {
        this.setState(state => ({
          observerEntries: {
            ...state.observerEntries,
            [entry.target.classList[0]]: false,
          },
        }))
      }
    }
  }

  initObserver = () => {
    if (!this._isMounted) return false
    if (typeof document === 'undefined') return false
    const els = document.querySelectorAll('[data-hide-feedback-widget="true"]')
    if (els.length === 0) return false

    const config = {
      root: null,
      rootMargin: '-10px',
      threshold: 0.01,
    }

    els.forEach(el => {
      const observer = new IntersectionObserver(this.handleObserver, config)
      observer.observe(el)
      this.observers.push(observer)
    })
  }

  disconnectObservers = () => {
    if (this.observers.length > 0) {
      this.observers.forEach(i => i.disconnect())
    }
  }

  componentWillUnmount() {
    this._isMounted = false
    this.disconnectObservers()
  }

  render() {
    const { t } = this.props

    return (
      <FeedbackDialog>
        {({ getToggleProps, setActiveView }) => (
          <Container
            style={{
              opacity: this.state.show ? 1 : 0,
              transform: this.state.show
                ? 'translate3d(0%,-50%,0)'
                : 'translate3d(100%,-50%,0)',
            }}
          >
            <Button
              {...getToggleProps({
                onClick: () =>
                  setActiveView(
                    sessionStorage.getItem('positive_feedback_submitted')
                      ? 'positive-confirm'
                      : 'feedback'
                  ),
              })}
            >
              <IconContainer>
                <Happy size={24} />
              </IconContainer>
              <IconContainer variant="danger">
                <Sad size={24} />
              </IconContainer>
              <Tooltip>
                <span>{t('common:feedbackDialog.title')}</span>
              </Tooltip>
            </Button>
          </Container>
        )}
      </FeedbackDialog>
    )
  }
}

export default withTranslation('common')(FeedbackWidget)
